/** Bounded, memory-only cache. A scope change discards every prior session result. */
export type ReadCacheOptions = {
  force?: boolean;
  ttlMs?: number;
  tags?: string[];
};
type Entry = { value: unknown; expires: number; tags: string[] };
type Pending = { promise: Promise<unknown>; tags: string[] };

export const queryKey = (url: string, params?: object | null): string => {
  const sorted = Object.entries(params || {})
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .sort(([a], [b]) => a.localeCompare(b));
  return `${url}?${JSON.stringify(sorted)}`;
};

export class ReadCache {
  private scope = "";
  private values = new Map<string, Entry>();
  private pending = new Map<string, Pending>();
  private generation = 0;
  constructor(
    private getScope: () => string,
    private maxEntries = 80,
  ) {}

  private syncScope() {
    const scope = this.getScope();
    if (scope !== this.scope) {
      this.invalidate();
      this.scope = scope;
    }
    return scope;
  }

  invalidate(tags?: string[]) {
    this.generation++;
    for (const [key, entry] of this.values) {
      if (!tags || entry.tags.some((tag) => tags.includes(tag)))
        this.values.delete(key);
    }
    for (const [key, entry] of this.pending) {
      if (!tags || entry.tags.some((tag) => tags.includes(tag)))
        this.pending.delete(key);
    }
  }

  peek<T>(key: string): T | undefined {
    this.syncScope();
    const entry = this.values.get(key);
    if (!entry || entry.expires <= Date.now()) return undefined;
    return entry.value as T;
  }

  get<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: ReadCacheOptions = {},
  ): Promise<T> {
    const scope = this.syncScope();
    const hit = this.values.get(key);
    if (!options.force && hit && hit.expires > Date.now()) {
      // Refresh insertion order for bounded least-recently-used eviction.
      this.values.delete(key);
      this.values.set(key, hit);
      return Promise.resolve(hit.value as T);
    }
    const running = this.pending.get(key);
    if (running) return running.promise as Promise<T>;
    const generation = this.generation;
    const tags = options.tags || [];
    const promise = Promise.resolve()
      .then(fetcher)
      .then((value) => {
        if (this.getScope() !== scope)
          throw new Error("Session changed while loading. Please try again.");
        if (generation === this.generation) {
          this.values.delete(key);
          this.values.set(key, {
            value,
            tags,
            expires: Date.now() + (options.ttlMs ?? 30000),
          });
          while (this.values.size > this.maxEntries)
            this.values.delete(this.values.keys().next().value as string);
        }
        return value;
      })
      .finally(() => {
        if (this.pending.get(key)?.promise === promise)
          this.pending.delete(key);
      });
    this.pending.set(key, { promise, tags });
    return promise;
  }
}
